(() => {
  'use strict';

  const formatMoney = (cents, currency) => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value / 100);
    } catch (_) {
      return `$${(value / 100).toFixed(2)}`;
    }
  };

  const variantOptions = (variant) => {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options.map((value) => String(value));
    return [variant.option1, variant.option2, variant.option3]
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value));
  };

  const initProduct = (section) => {
    if (section.dataset.productFormReady === 'true') return;

    const productJson = section.querySelector('[data-product-json]');
    const form = section.querySelector('[data-product-form]');
    const select = section.querySelector('[data-variant-select]');
    if (!productJson || !form || !select) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch (_) {
      return;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const optionGroups = [...section.querySelectorAll('[data-option-group]')];
    if (!variants.length || !optionGroups.length) return;

    let galleryImages = [];
    try {
      galleryImages = JSON.parse(section.querySelector('[data-gallery-json]')?.textContent || '[]');
    } catch (_) {
      galleryImages = [];
    }

    const price = section.querySelector('[data-product-price]');
    const priceValue = section.querySelector('[data-product-price-value]');
    const comparePrice = section.querySelector('[data-product-compare-price]');
    const submit = form.querySelector('[data-add-to-cart]');
    const status = form.querySelector('[data-product-form-status]');
    const mainImage = section.querySelector('[data-gallery-main] img, img[data-gallery-main]');
    const thumbnails = [...section.querySelectorAll('[data-gallery-id]')];
    const configuredPreviewOnly = form.dataset.previewOnly === 'true' || form.dataset.testOnly === 'true';
    const globalPreviewOnly = Boolean(window.DLM && (window.DLM.previewOnly === true || window.DLM.previewOnly === 'true'));
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const previewOnly = configuredPreviewOnly || globalPreviewOnly || tags.includes('development-test-only');
    const currency = (window.DLM && window.DLM.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    let currentVariant = null;

    const announce = (message) => {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
    };

    const setGalleryImage = (image) => {
      if (!image || !mainImage) return;
      const source = image.src || image.url;
      if (!source) return;

      mainImage.src = source;
      mainImage.alt = image.alt || product.title || '';
      if (image.srcset) {
        mainImage.srcset = image.srcset;
        if (image.sizes) mainImage.sizes = image.sizes;
      } else {
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
      }

      const imageId = String(image.id || '');
      thumbnails.forEach((button) => {
        const active = imageId && String(button.dataset.galleryId) === imageId;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    };

    const variantImage = (variant) => {
      if (!variant) return null;
      const featuredImage = variant.featured_image || (variant.featured_media && variant.featured_media.preview_image);
      const featuredId = featuredImage && featuredImage.id;
      return galleryImages.find((image) => String(image.id) === String(featuredId)) || featuredImage || null;
    };

    const selectedValues = () => optionGroups.map((group) => {
      const checked = group.querySelector('[data-option-value]:checked');
      return checked ? String(checked.value) : null;
    });

    const matchesSelections = (variant, selections, ignoredIndex) => {
      const values = variantOptions(variant);
      return selections.every((value, index) => index === ignoredIndex || value === null || values[index] === value);
    };

    const exactVariant = (selections) => variants.find((variant) => {
      const values = variantOptions(variant);
      return selections.length === values.length && selections.every((value, index) => value !== null && values[index] === value);
    }) || null;

    const syncOptionAvailability = () => {
      const selections = selectedValues();
      optionGroups.forEach((group, index) => {
        group.querySelectorAll('[data-option-value]').forEach((input) => {
          const possibleVariants = variants.filter((variant) => {
            const values = variantOptions(variant);
            return values[index] === String(input.value) && selections.every((selected, previousIndex) => previousIndex >= index || selected === null || values[previousIndex] === selected);
          });
          const possible = possibleVariants.length > 0;
          const soldOut = possible && !possibleVariants.some((variant) => Boolean(variant.available));
          const label = section.querySelector(`label[for="${input.id}"]`);
          const state = label && label.querySelector('[data-option-state]');

          input.disabled = !possible;
          input.setAttribute('aria-disabled', String(!possible));
          input.dataset.soldOut = String(soldOut);
          if (label) {
            label.classList.toggle('is-impossible', !possible);
            label.classList.toggle('is-sold-out', soldOut);
          }
          if (state) state.textContent = !possible ? ' Not available in this combination.' : (soldOut ? ' Sold out.' : '');
        });
      });
    };

    const syncControlsFromVariant = (variant) => {
      const values = variantOptions(variant);
      optionGroups.forEach((group, index) => {
        const input = [...group.querySelectorAll('[data-option-value]')]
          .find((control) => String(control.value) === values[index]);
        if (input) input.checked = true;
      });
    };

    const setSubmitLabel = (text) => {
      if (!submit) return;
      const label = submit.querySelector('[data-add-to-cart-label]');
      (label || submit).textContent = text;
    };

    const setVariant = (variant, replaceUrl) => {
      currentVariant = variant || null;
      const available = Boolean(variant && variant.available);

      if (variant) {
        const formattedPrice = formatMoney(variant.price, currency);
        if (priceValue) priceValue.textContent = formattedPrice;
        else if (price) price.textContent = formattedPrice;
      }

      if (comparePrice) {
        const hasComparePrice = Boolean(variant) && Number(variant.compare_at_price) > Number(variant.price);
        comparePrice.hidden = !hasComparePrice;
        comparePrice.textContent = hasComparePrice ? formatMoney(variant.compare_at_price, currency) : '';
      }

      if (submit) {
        submit.disabled = previewOnly || !available;
        setSubmitLabel(previewOnly ? 'Preview only – not for sale' : (available ? 'Add to bag' : 'Unavailable'));
      }

      announce(previewOnly ? 'This product is shown for preview only and cannot be added to the bag.' : (available ? '' : 'This option is unavailable.'));

      if (variant) {
        const image = variantImage(variant);
        if (image) setGalleryImage(image);
      }

      if (replaceUrl && variant && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      }
    };

    const selectVariantFromControls = (changedIndex) => {
      const selections = selectedValues();
      let variant = exactVariant(selections);
      if (!variant) {
        const candidates = variants.filter((item) => {
          const values = variantOptions(item);
          return selections.every((selected, index) => index > changedIndex || values[index] === selected);
        });
        variant = candidates.find((item) => item.available) || candidates[0] || null;
        if (variant) syncControlsFromVariant(variant);
      }
      syncOptionAvailability();
      if (!variant) {
        currentVariant = null;
        if (submit) {
          submit.disabled = true;
          setSubmitLabel(previewOnly ? 'Preview only – not for sale' : 'Unavailable');
        }
        announce(previewOnly ? 'This product is shown for preview only and cannot be added to the bag.' : 'Choose an available option combination.');
        return;
      }

      select.value = String(variant.id);
      setVariant(variant, true);
    };

    optionGroups.forEach((group) => {
      group.addEventListener('change', (event) => {
        if (event.target && event.target.matches('[data-option-value]')) selectVariantFromControls(Number(group.dataset.optionIndex));
      });
    });

    select.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === String(select.value));
      if (!variant) return;
      syncControlsFromVariant(variant);
      syncOptionAvailability();
      setVariant(variant, true);
    });

    thumbnails.forEach((button) => button.addEventListener('click', () => {
      const image = galleryImages.find((item) => String(item.id) === String(button.dataset.galleryId));
      if (image) setGalleryImage(image);
    }));

    form.addEventListener('submit', (event) => {
      if (previewOnly || !currentVariant || !currentVariant.available) {
        event.preventDefault();
        announce(previewOnly ? 'Preview only – not for sale.' : 'This option is unavailable.');
      }
    });

    form.addEventListener('dlm:cart-complete', () => {
      if (!submit) return;
      const available = Boolean(currentVariant && currentVariant.available);
      submit.disabled = previewOnly || !available;
      setSubmitLabel(previewOnly ? 'Preview only – not for sale' : (available ? 'Add to bag' : 'Unavailable'));
    });

    const initialVariant = variants.find((variant) => String(variant.id) === String(select.value)) || variants[0];
    syncControlsFromVariant(initialVariant);
    syncOptionAvailability();
    setVariant(initialVariant, false);
    section.classList.add('is-product-form-ready');
    section.dataset.productFormReady = 'true';
  };

  const boot = () => document.querySelectorAll('[data-product-section]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  window.addEventListener('dlm:section-load', boot);
})();

/* DLM product gallery enlargement — October 19. */
(() => {
  const initZoom = () => {
    document.querySelectorAll('[data-gallery-main]').forEach((gallery) => {
      const image = gallery.querySelector('img');
      if (!image || gallery.querySelector('.product-image-enlarge')) return;
      const section = gallery.closest('main') || document;
      let images = [];
      try { images = JSON.parse(section.querySelector('[data-gallery-json]')?.textContent || '[]'); } catch (_) { images = []; }
      if (Array.isArray(images)) {
        const thumbnails = section.querySelectorAll('[data-gallery-id]');
        thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => {
          const selected = images.find((item) => String(item.id) === thumbnail.dataset.galleryId);
          if (!selected?.src) return;
          image.src = selected.src;
          image.alt = selected.alt || image.alt;
          if (selected.srcset) image.srcset = selected.srcset;
          else image.removeAttribute('srcset');
          if (selected.sizes) image.sizes = selected.sizes;
          thumbnails.forEach((item) => {
            const active = item === thumbnail;
            item.classList.toggle('is-active', active);
            if (active) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          });
        }));
      }
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'product-image-enlarge';
      trigger.setAttribute('aria-label', 'Enlarge product image');
      image.before(trigger);
      trigger.append(image);
      const hint = document.createElement('span');
      hint.className = 'product-image-zoom-hint';
      hint.textContent = 'Click image to enlarge';
      trigger.append(hint);
      trigger.addEventListener('click', () => {
        let modal = document.getElementById('DlmProductImageZoom');
        if (!modal) {
          modal = document.createElement('dialog');
          modal.id = 'DlmProductImageZoom';
          modal.className = 'product-image-zoom';
          modal.setAttribute('aria-label', 'Enlarged product image');
          const close = document.createElement('button');
          close.type = 'button';
          close.className = 'product-image-zoom-close';
          close.textContent = 'Close';
          close.setAttribute('autofocus', '');
          const enlarged = document.createElement('img');
          enlarged.className = 'product-image-zoom-photo';
          modal.append(close, enlarged);
          document.body.append(modal);
          close.addEventListener('click', () => modal.close());
          modal.addEventListener('click', (event) => {
            if (event.target !== modal) return;
            const box = modal.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) modal.close();
          });
          modal.addEventListener('close', () => document.documentElement.classList.remove('product-image-zoom-open'));
        }
        const enlarged = modal.querySelector('img');
        enlarged.src = image.src;
        enlarged.alt = image.alt;
        document.documentElement.classList.add('product-image-zoom-open');
        modal.showModal();
        modal.addEventListener('close', () => trigger.focus(), { once: true });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initZoom, { once: true });
  else initZoom();
  document.addEventListener('shopify:section:load', initZoom);
})();
